import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronDown, ChevronUp } from 'lucide-react';
import { useGetFAQQuery } from "../../../hooks/useCommon"
import { useNavigate } from "react-router-dom"
import { useStore } from '@tanstack/react-store';
import { themeStore } from '../../../stores/theme';

const FAQComponent = () => {
    const { isDarkMode } = useStore(themeStore)
    const [expandedItems, setExpandedItems] = useState(new Set());
    const [questions, setQuestions] = useState([])
    const { data: faqData, isSuccess: isFaqSuccess } = useGetFAQQuery(`?language=en`)
    const navigate = useNavigate()

    useEffect(() => {
        if (isFaqSuccess) {
            const faqList = faqData?.data?.data || [];
            setQuestions(faqList?.filter((q) => q?.status === 1));
            if (faqList.length > 0) {
                setExpandedItems(new Set([faqList[0]._id])); // default expand first item safely
            }
        }
    }, [faqData, isFaqSuccess])

    const toggleExpanded = (id) => {
        const newExpandedItems = new Set(expandedItems);
        if (newExpandedItems.has(id)) {
            newExpandedItems.delete(id);
        } else {
            newExpandedItems.add(id);
        }
        setExpandedItems(newExpandedItems);
    };

    const handleBackClick = () => {
        navigate(-1)
    };

    // Dynamically split questions into 2 columns
    const midpoint = Math.ceil(questions.length / 2);
    const leftColumn = questions.slice(0, midpoint);
    const rightColumn = questions.slice(midpoint);

    return (
        <div className={`min-h-screen ${isDarkMode?"bg-gray-800": "bg-white"}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {/* Header */}
                <div className="flex items-center mb-8">
                    <button
                        onClick={handleBackClick}
                        className={`${isDarkMode? "bg-gray-800": "bg-white" } mb-8 p-3 border-2 border-blue-500 rounded-lg transition-colors shadow-sm`}>
                        <ChevronLeft className="w-6 h-6 text-blue-600" />
                    </button>
                    <h1 className={`text-3xl font-bold ${isDarkMode?"text-white": "text-gray-800"} text-center flex-1`}>FAQ</h1>
                </div>
                {/* FAQ Container */}
                <div className={`${isDarkMode?"bg-gray-800 border-gray-500": "bg-white border-gray-100"} rounded-3xl border-2 p-8 shadow-lg`}>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Left Column */}
                        <div className="space-y-4">
                            {leftColumn?.map((faq, index) => (
                                <FAQItem
                                    index={index}
                                    key={faq?._id}
                                    faq={faq}
                                    isExpanded={expandedItems.has(faq?._id)}
                                    onToggle={() => toggleExpanded(faq?._id)}
                                    isDarkMode={isDarkMode}
                                />
                            ))}
                        </div>

                        {/* Right Column */}
                        <div className="space-y-4">
                            {rightColumn?.map((faq, index) => (
                                <FAQItem
                                    index={midpoint + index}
                                    key={faq?._id}
                                    faq={faq}
                                    isExpanded={expandedItems.has(faq?._id)}
                                    onToggle={() => toggleExpanded(faq?._id)}
                                    isDarkMode={isDarkMode}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const FAQItem = ({ index, faq, isExpanded, onToggle, isDarkMode }) => {
    return (
        <div className={`self-start ${!isExpanded && "md:h-[90px]"} border border-gray-200 rounded-2xl p-2 ${isDarkMode? "bg-gray-900": "bg-white"} shadow-md  ${isExpanded ? 'bg-blue-50 border-blue-400' : 'bg-gray-50'}`}>
            <button
                onClick={onToggle}
                className="w-full p-4 text-left flex items-start justify-between">
                <div className="flex-1 pr-4">
                    <span className={`font-semibold ${isDarkMode? "text-white": "text-gray-900"} mr-2`}>Q{(index) + 1}.</span>
                    <span className={`${isDarkMode? "text-white": "text-gray-900"} leading-relaxed`}>{faq.question}</span>
                </div>
                <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${isExpanded
                    ? 'bg-blue-500 text-white'
                    : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                    }`}>
                    {isExpanded ? (
                        <ChevronUp className="w-5 h-5" />
                    ) : (
                        <ChevronDown className="w-5 h-5" />
                    )}
                </div>
            </button>

            {/* Expandable Answer Section */}
            <div className={`overflow-hidden ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}>
                {faq.answer && (
                    <div className="px-4 pb-4">
                        <div className="pl-8 pr-14">
                            <p className={`${isDarkMode? "text-white": "text-gray-900"} leading-relaxed`}>
                                {faq.answer}
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FAQComponent;